import { useState, useEffect, useId, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Archive } from 'lucide-react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';
import { getTagColorClass } from '../../utils/tagColors';
import { PriorityDropdown } from '../shared/fields/priority-icon';
import { ComplexityDots } from '../shared/fields/complexity-dots';
import { initials } from '../shared/ui/InitialsAvatar';
import { dueDateIso, formatChangedDateTime, formatChangedPrecise, formatDueDate, isOverdue } from './kanbanRowValues';
import { TaskStateDropdown as StatusCell } from '../task/TaskStateDropdown';
import ConfirmTrashModal from '../shared/ui/ConfirmTrashModal';

// ── TagList ─────────────────────────────────────────────────────────────────
// Local state drives the display immediately; onTagsChange propagates to the store.
// readOnly hides add/remove affordances (archived rows).
const TagList = ({ tags: initialTags, tagOptions, onTagsChange, readOnly = false }) => {
    const [tags, setTags] = useState(initialTags);
    const [isAdding, setIsAdding] = useState(false);
    const [input, setInput] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        if (!isAdding) return;
        const h = e => { if (ref.current && !ref.current.contains(e.target)) setIsAdding(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [isAdding]);

    const resolveLabel = tid => tagOptions.find(o => String(o.value) === String(tid))?.label ?? String(tid);
    const resolveTid = name => tagOptions.find(o => o.label === name)?.value ?? name;

    const available = tagOptions
        .map(o => o.label)
        .filter(l => !tags.some(t => String(t) === String(resolveTid(l))) && l.toLowerCase().includes(input.toLowerCase()));

    const handleAdd = name => {
        if (readOnly) return;
        const tid = resolveTid(name);
        if (tid && !tags.some(t => String(t) === String(tid))) {
            const next = [...tags, tid];
            setTags(next);
            onTagsChange(next);
        }
        setIsAdding(false); setInput('');
    };

    const handleRemove = idx => {
        if (readOnly) return;
        const next = tags.filter((_, i) => i !== idx);
        setTags(next);
        onTagsChange(next);
    };

    return (
        <div className="flex flex-wrap gap-1 mb-1 relative" onPointerDown={e => e.stopPropagation()}>
            {tags.map((tid, idx) => {
                const label = resolveLabel(tid);
                return (
                    <span key={idx} className={`group relative px-2 py-0.5 rounded text-[10px] font-medium ${readOnly ? '' : 'pr-4'} ${getTagColorClass(label)}`}>
                        {label}
                        {!readOnly && (
                            <button
                                onClick={e => { e.stopPropagation(); handleRemove(idx); }}
                                className="absolute right-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-0.5 hover:bg-black/10 rounded-full transition-all"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </span>
                );
            })}
            {!readOnly && (isAdding ? (
                <div ref={ref} className="relative">
                    <input
                        autoFocus type="text" value={input}
                        onChange={e => setInput(e.target.value)}
                        className="w-24 px-1 py-0.5 text-[10px] border rounded shadow-sm focus:ring-1 focus:ring-iris-500 focus:border-iris-500 outline-none"
                        placeholder="Tag..."
                        onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleAdd(input.trim()); }
                            else if (e.key === 'Escape') setIsAdding(false);
                        }}
                        onClick={e => e.stopPropagation()}
                    />
                    {available.length > 0 && (
                        <div className="absolute top-full left-0 mt-1 w-32 bg-white border border-slate-200 rounded shadow-lg z-50 max-h-32 overflow-y-auto">
                            {available.map(tag => (
                                <div key={tag} className="px-2 py-1 text-[10px] hover:bg-iris-50 cursor-pointer text-slate-700" onClick={e => { e.stopPropagation(); handleAdd(tag); }}>
                                    {tag}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <button
                    onClick={e => { e.stopPropagation(); setIsAdding(true); setInput(''); }}
                    className="px-2 py-0.5 rounded text-[10px] bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-iris-600 border border-dashed border-slate-300 transition-colors"
                    title="Ajouter un tag"
                >+</button>
            ))}
        </div>
    );
};

// StatusCell vit désormais dans components/task/TaskStateDropdown (partagé avec
// le bloc « Tâches » des fiches CRM) — importé sous son nom d'origine ci-dessus.
// ── DescriptionTooltip ──────────────────────────────────────────────────────
// Infobulle de description au survol du titre, en portail pour ne pas être
// rognée par la cellule. Rien à afficher = rien à rendre : pas d'infobulle vide.
// a11y (WCAG 2.2 §1.4.13) : ouverture aussi au focus clavier, le panneau reste
// ouvert quand le pointeur passe dessus, Échap ferme.
const TOOLTIP_OPEN_DELAY = 250;
const TOOLTIP_CLOSE_DELAY = 150;
const TOOLTIP_WIDTH = 320;

const DescriptionTooltip = ({ html, children }) => {
    // Le champ est du texte riche ; l'infobulle affiche du texte brut (jamais
    // dangerouslySetInnerHTML sur du contenu saisi par un utilisateur).
    const text = String(html ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    const panelId = useId();
    const anchorRef = useRef(null);
    const openTimer = useRef(null);
    const closeTimer = useRef(null);
    const [coords, setCoords] = useState(null);

    useEffect(() => () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); }, []);

    useEffect(() => {
        if (!coords) return undefined;
        const dismiss = () => {
            clearTimeout(openTimer.current);
            clearTimeout(closeTimer.current);
            setCoords(null);
        };
        const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', dismiss, true);
        return () => {
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', dismiss, true);
        };
    }, [coords]);

    if (!text) return children;

    const open = () => {
        clearTimeout(closeTimer.current);
        openTimer.current = setTimeout(() => {
            const el = anchorRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            const left = Math.max(8, Math.min(r.left, window.innerWidth - TOOLTIP_WIDTH - 8));
            // Bascule au-dessus quand la ligne est basse dans la fenêtre.
            const flipUp = window.innerHeight - r.bottom < 180 && r.top > 180;
            setCoords(flipUp ? { bottom: window.innerHeight - r.top + 6, left } : { top: r.bottom + 6, left });
        }, TOOLTIP_OPEN_DELAY);
    };
    const close = () => {
        clearTimeout(openTimer.current);
        closeTimer.current = setTimeout(() => setCoords(null), TOOLTIP_CLOSE_DELAY);
    };

    return (
        <>
            <span
                ref={anchorRef}
                onMouseEnter={open}
                onMouseLeave={close}
                onFocus={open}
                onBlur={() => { clearTimeout(openTimer.current); setCoords(null); }}
                aria-describedby={coords ? panelId : undefined}
            >
                {children}
            </span>
            {coords && createPortal(
                <div
                    id={panelId}
                    role="tooltip"
                    style={{ position: 'fixed', ...coords, width: TOOLTIP_WIDTH, zIndex: 9999 }}
                    className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-700 leading-relaxed max-h-60 overflow-y-auto"
                    onMouseEnter={() => clearTimeout(closeTimer.current)}
                    onMouseLeave={close}
                >
                    <p className="break-words">{text}</p>
                </div>,
                document.body,
            )}
        </>
    );
};

// ── TitleCell ───────────────────────────────────────────────────────────────
// Le titre en lien vers la fiche + un crayon (révélé au survol de la ligne) qui
// bascule en édition sur place. Entrée valide, Échap annule, la perte de focus
// valide aussi — on n'enferme jamais l'utilisateur dans un champ ouvert.
// L'écriture passe par apiData.data.title, donc c'est le DataSync existant de la
// sous-racine qui persiste, comme pour les étoiles ou les tags : aucun second
// chemin d'enregistrement.
const TitleCell = ({ taskId, title, description, readOnly, onRename }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(title);
    const inputRef = useRef(null);

    // Un rafraîchissement du board (ou une édition ailleurs) doit se voir ici.
    useEffect(() => { if (!editing) setDraft(title); }, [title, editing]);
    useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

    const commit = () => {
        const next = draft.trim();
        setEditing(false);
        // Un titre vide n'est pas un titre : on revient à l'ancien.
        if (!next || next === title) { setDraft(title); return; }
        onRename(next);
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                autoFocus
                type="text"
                value={draft}
                aria-label="Titre de la tâche"
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') { e.preventDefault(); commit(); }
                    else if (e.key === 'Escape') { setDraft(title); setEditing(false); }
                }}
                onClick={(e) => e.stopPropagation()}
                className="input h-7 py-0 text-sm w-full max-w-md"
            />
        );
    }

    return (
        <div className="flex items-center gap-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">
                <DescriptionTooltip html={description}>
                    <a
                        href={`/task-edit?id=${taskId}`}
                        className="hover:text-iris-600 hover:underline"
                        onClick={e => e.stopPropagation()}
                    >
                        {title}
                    </a>
                </DescriptionTooltip>
            </div>
            {!readOnly && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDraft(title); setEditing(true); }}
                    // Révélé au survol de la ligne (le `group` est le <tr>), mais
                    // toujours atteignable au clavier : opacity, pas de `hidden`.
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded text-slate-400 hover:text-iris-600 hover:bg-iris-50 transition-opacity flex-shrink-0"
                    title="Modifier le titre"
                    aria-label={`Modifier le titre : ${title}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    </svg>
                </button>
            )}
        </div>
    );
};

// ── AssigneeCell ────────────────────────────────────────────────────────────
// Assignation en place : un menu portalisé des MEMBRES du projet (jamais tout
// l'annuaire — assigner un non-membre serait refusé côté serveur), plus
// « Non assigné » pour retirer. Même contrat d'ouverture/fermeture que
// StatusCell et PriorityDropdown : mousedown hors bouton ET hors menu ferme.
const AssigneeCell = ({ assignee, users, assignableUsers, readOnly, onAssign }) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = () => setOpen(false);
        const onDoc = (e) => {
            if (btnRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // L'assigné est stocké tantôt en id, tantôt en nom : on résout sur les deux.
    const user = users.find(u => String(u.id) === String(assignee) || u.name === assignee);
    const userName = user?.name ?? (typeof assignee === 'string' ? assignee : '');
    const options = assignableUsers.length ? assignableUsers : users;

    const label = userName
        ? (
            <span className="flex items-center gap-2">
                <span className="avatar text-xs">{initials(userName)}</span>
                <span className="text-sm text-slate-700">{userName}</span>
            </span>
        )
        : <span className="text-sm text-slate-400">Non assigné</span>;

    if (readOnly) return label;

    const toggle = (e) => {
        e.stopPropagation();
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setCoords({ top: r.bottom + 4, left: r.left });
        }
        setOpen(o => !o);
    };

    const choose = (value) => { setOpen(false); onAssign(value); };

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={toggle}
                aria-haspopup="listbox"
                aria-expanded={open}
                title="Changer l'assigné"
                className="flex items-center gap-2 rounded px-1 py-0.5 -ml-1 hover:bg-slate-100 cursor-pointer"
            >
                {label}
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-slate-400"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {open && coords && createPortal(
                <ul
                    ref={menuRef}
                    role="listbox"
                    style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999 }}
                    className="bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[200px] max-h-60 overflow-y-auto"
                >
                    <li
                        role="option"
                        aria-selected={!userName}
                        onClick={() => choose(null)}
                        className="px-3 py-1.5 text-xs text-slate-500 hover:bg-iris-50 hover:text-iris-700 cursor-pointer"
                    >
                        Non assigné
                    </li>
                    {options.map(u => (
                        <li
                            key={u.id}
                            role="option"
                            aria-selected={String(u.id) === String(user?.id)}
                            onClick={() => choose(u.id)}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-iris-50 hover:text-iris-700 cursor-pointer"
                        >
                            <span className="avatar text-xs">{initials(u.name)}</span>
                            {u.name}
                        </li>
                    ))}
                </ul>,
                document.body,
            )}
        </>
    );
};

// ── KanbanListRow — ReactiveJSON plugin (bridge) ────────────────────────────
//
// Rendered inside a per-task ReactiveJsonSubroot whose store contains:
//   ~~.apiData   — Syncable: { submission_url, data: { tags, priority, complexity } }
//   ~~.display   — read-only: { id, title, columnId, state, assignee, type, parentId,
//                  depth, parentTitle, isSubtask, demande_evolution, archived flags }
//   ~~.columns, ~~.users, ~~.tagOptions, ~~.typeOptions, ~~.returnTypeOptions
//
// The sibling DataSync (path ~~.apiData, onIdle) persists writes to apiData.data
// after 1.5 s. Edits also dispatch kanbanCardUpdated so the board's in-memory
// tasks (and thus the grid cards) reflect the change without a reload.
export const KanbanListRow = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const ev = key => evaluateTemplateValue({ valueToEvaluate: key, globalDataContext, templateContext });

    // Confirmation d'archivage : même modale que la carte du board et que la fiche.
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

    const display = ev('~~.display') ?? {};
    const apiData = ev('~~.apiData') ?? {};
    const columns = ev('~~.columns') ?? [];
    const users = ev('~~.users') ?? [];
    const tagOptions = ev('~~.tagOptions') ?? [];
    const typeOptions = ev('~~.typeOptions') ?? [];
    const returnTypeOptions = ev('~~.returnTypeOptions') ?? [];
    const assignableUsers = ev('~~.assignableUsers') ?? [];

    const tags = apiData.data?.tags ?? [];
    const priority = apiData.data?.priority ?? 0;
    const complexity = apiData.data?.complexity ?? 0;

    const typeOption = typeOptions.find(o => String(o.value) === String(display.type));
    const typeLabel = typeOption?.label ?? '';
    const typeParentId = Number(typeOption?.data?.parentId ?? 0);
    const hasSubTypes = !typeParentId && typeOptions.some(o => Number(o.data?.parentId) === Number(display.type));
    const typeCls = hasSubTypes
        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
        : typeParentId > 0
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : 'bg-sky-100 text-sky-700 border-sky-200';

    const returnTypeLabel = returnTypeOptions.find(o => String(o.value) === String(display.demande_evolution))?.label ?? '';

    const isArchived = !!display.isArchived;
    const canUnarchive = !!display.canUnarchive;
    // Projet archivé = tout est en lecture seule : pas d'action d'archivage.
    const projectArchived = !!display.projectArchived;

    // Grouping / subtask hints (structural): depth drives the indent, isSubtask the
    // "Sous-tâche de X" cue — NOT driven by the (ambiguous) yellow type badge alone.
    const depth = Number(display.depth) || 0;
    const isSubtask = !!display.isSubtask || typeParentId > 0;
    const parentTitle = display.parentTitle || '';

    // Date de dernière modification (base field `changed`, exposé par le view
    // display decoupled_card) : date + heure dans la cellule, seconde près dans
    // l'infobulle. Reste figée après une édition en ligne jusqu'au rechargement.
    // Contenus liés (entreprise, transaction, contact…) : le back en joint le
    // type et le titre, le mappeur front l'URL de la fiche (cf. mapLinkedContent).
    const linkedContentRefs = display.linkedContentRefs ?? [];
    const changedLabel = formatChangedDateTime(display.changed);
    const changedTitle = formatChangedPrecise(display.changed);
    // Échéance (field_due_date, date seule) : rouge quand elle est dépassée —
    // même règle que la carte du board (strictement avant aujourd'hui).
    const dueLabel = formatDueDate(display.dueDate);
    const dueOverdue = isOverdue(display.dueDate);
    const indentStyle = depth ? { paddingLeft: `${depth * 1.5}rem` } : undefined;

    // Immediate board reflection: mirror inline edits into the board's in-memory
    // tasks via the same event the card SyncBridge uses. `rating` is the UI display
    // key the cards read for priority (kept in sync with the `priority` write key).
    const emitCardUpdate = (updates) => {
        if (display.id == null) { console.warn('KanbanListRow: cannot emit kanbanCardUpdated, missing task id'); return; }
        document.dispatchEvent(new CustomEvent('kanbanCardUpdated', { detail: { taskId: display.id, updates } }));
    };

    // Archivage : même canal que la carte du board (`kanbanArchiveTask`), donc
    // même mise à jour optimiste, même refresh et même message d'erreur 403 —
    // tous portés par KanbanBoard. Rien n'est écrit directement ici.
    const handleArchive = () => {
        setShowArchiveConfirm(false);
        if (display.id == null) { console.warn('KanbanListRow: cannot archive, missing task id'); return; }
        document.dispatchEvent(new CustomEvent('kanbanArchiveTask', { detail: { taskId: display.id } }));
    };

    const handleRating = star => {
        if (isArchived) return;  // archived rows are read-only
        globalDataContext?.updateData?.(star, 'apiData.data.priority');
        emitCardUpdate({ rating: star, priority: star });
    };

    const handleComplexity = level => {
        if (isArchived) return;
        globalDataContext?.updateData?.(level, 'apiData.data.complexity');
        emitCardUpdate({ complexity: level });
    };

    const handleRename = (nextTitle) => {
        if (isArchived) return;
        globalDataContext?.updateData?.(nextTitle, 'apiData.data.title');
        emitCardUpdate({ title: nextTitle });
    };

    const handleAssign = (userId) => {
        if (isArchived) return;
        const value = userId == null || userId === '' ? null : userId;
        globalDataContext?.updateData?.(value, 'apiData.data.assignee');
        emitCardUpdate({ assignee: value });
    };

    const handleTags = newTids => {
        if (isArchived) return;
        globalDataContext?.updateData?.(newTids, 'apiData.data.tags');
        emitCardUpdate({ tags: newTids });
    };

    return (
        <tr className={`group ${isArchived ? 'opacity-60 bg-amber-50/40' : 'hover:bg-slate-50'}`}>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2" style={indentStyle}>
                    {typeLabel && (
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${typeCls}`}>
                            {typeLabel}
                        </span>
                    )}
                    <TitleCell
                        taskId={display.id}
                        title={display.title}
                        description={display.description}
                        readOnly={isArchived}
                        onRename={handleRename}
                    />
                    {isArchived && (
                        <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-amber-100 text-amber-700 border-amber-200 whitespace-nowrap">
                            {display.archivedLabel || 'Archivé'}
                        </span>
                    )}
                    {isArchived && canUnarchive && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); document.dispatchEvent(new CustomEvent('kanbanUnarchiveTask', { detail: { taskId: display.id } })); }}
                            className="text-xs text-amber-600 hover:text-green-600 hover:underline whitespace-nowrap"
                            title="Désarchiver"
                        >
                            Désarchiver
                        </button>
                    )}
                    {/* Discret comme sur la carte du board (révélé au survol de la ligne,
                        qui porte `group`), mais AUSSI au focus clavier : `opacity-0` seul
                        laisserait un bouton tabulable et invisible (WCAG 2.4.7). */}
                    {!isArchived && !projectArchived && (
                        <button
                            type="button"
                            onClick={(ev) => { ev.stopPropagation(); setShowArchiveConfirm(true); }}
                            className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-1 rounded text-slate-500 hover:text-sky-600 hover:bg-sky-50 transition-colors"
                            title="Archiver la tâche"
                            aria-label={`Archiver la tâche « ${display.title || 'sans titre'} »`}
                        >
                            <Archive size={14} aria-hidden="true" />
                        </button>
                    )}
                    {showArchiveConfirm && createPortal(
                        <ConfirmTrashModal
                            isOpen
                            onConfirm={handleArchive}
                            onCancel={() => setShowArchiveConfirm(false)}
                            title={`Archiver « ${display.title || 'cette tâche'} » ?`}
                            description="Cette tâche sera archivée en lecture seule. Vous pourrez la désarchiver à tout moment."
                            confirmLabel="Archiver"
                            variant="archive"
                        />,
                        document.body,
                    )}
                </div>
                <div style={indentStyle}>
                    {isSubtask && (
                        <div className="text-[11px] text-slate-500 mt-0.5">↳ {parentTitle ? `Sous-tâche de ${parentTitle}` : 'Sous-tâche'}</div>
                    )}
                    {returnTypeLabel && <div className="text-sm text-slate-500 mt-1">{returnTypeLabel}</div>}
                </div>
            </td>

            <td className="px-6 py-4 whitespace-nowrap">
                <StatusCell taskId={display.id} typeId={display.type} state={display.state} columns={columns} columnId={display.columnId} isArchived={isArchived} />
            </td>

            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                {/* CDC-TMA T12: Jira-style priority dropdown, consistent with the card/fiche. */}
                <PriorityDropdown value={priority} onChange={(level) => handleRating(level)} size={18} readOnly={isArchived} />
            </td>

            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                {/* Parity with cards: editable complexity dots. */}
                <ComplexityDots value={complexity} onChange={handleComplexity} size={14} readOnly={isArchived} />
            </td>

            {/* Échéance — rouge quand elle est dépassée. La mention « dépassée »
                double la couleur pour qui ne la perçoit pas (RGAA 3.1). */}
            <td className="px-6 py-4 whitespace-nowrap text-sm">
                {dueLabel ? (
                    <time
                        dateTime={dueDateIso(display.dueDate)}
                        className={dueOverdue ? 'font-semibold text-rose-600' : 'text-slate-500'}
                    >
                        {dueLabel}
                        {dueOverdue && <span className="sr-only"> (échéance dépassée)</span>}
                    </time>
                ) : (
                    <span className="text-slate-300">—</span>
                )}
            </td>

            <td className="px-6 py-4 whitespace-nowrap">
                <AssigneeCell
                    assignee={display.assignee}
                    users={users}
                    assignableUsers={assignableUsers}
                    readOnly={isArchived}
                    onAssign={handleAssign}
                />
            </td>

            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                <TagList tags={tags} tagOptions={tagOptions} onTagsChange={handleTags} readOnly={isArchived} />
            </td>

            {/* Contenu lié — la ou les fiches auxquelles la tâche se rapporte
                (field_linked_content). Même puce que la carte du board : titre
                entier au-dessus, badge de type dessous à gauche. `whitespace-normal`
                (et non le `whitespace-nowrap` des autres cellules) : un titre de
                fiche est long, on le laisse passer à la ligne plutôt que d'étirer
                le tableau. */}
            <td className="px-6 py-4 text-sm text-slate-500 whitespace-normal align-top">
                {linkedContentRefs.length === 0
                    ? <span className="text-slate-300">—</span>
                    : (
                        <div className="flex flex-col gap-2">
                            {linkedContentRefs.map((ref) => (
                                <div key={ref.id} className="flex flex-col items-start gap-1 min-w-0">
                                    {ref.url ? (
                                        <a href={ref.url} className="text-sm font-medium text-iris-600 hover:underline break-words">{ref.title}</a>
                                    ) : (
                                        /* Bundle sans écran dédié dans le SPA : pas de lien mort. */
                                        <span className="text-sm font-medium text-slate-700 break-words">{ref.title}</span>
                                    )}
                                    <span className="badge badge-neutral badge-sm">{ref.typeLabel}</span>
                                </div>
                            ))}
                        </div>
                    )}
            </td>

            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                {changedLabel
                    ? <time dateTime={display.changed} title={changedTitle}>{changedLabel}</time>
                    : <span className="text-slate-300">—</span>}
            </td>
        </tr>
    );
};
